import React, { useEffect } from "react";
import { LOGO_URL, PHONE_DISPLAY, EMAIL } from "../lib/constants";

const GOLD = "#c9a227";
const BLACK = "#0a0a0a";

/**
 * /investors — Private confidential business plan / investor brief.
 * Not linked anywhere. Shared via direct URL only.
 * Print-optimised: nav + floating WhatsApp hidden via @media print.
 * NO investment figures intentionally — investor names the ask.
 */
const Investors = () => {
    useEffect(() => {
        document.title =
            "The Reset Sanctuary — Confidential Business Plan";
    }, []);

    return (
        <main
            data-testid="investors-page"
            style={{
                backgroundColor: BLACK,
                color: "#fff",
                fontFamily: "Inter, sans-serif",
                minHeight: "100vh",
                padding: "60px 24px 80px",
            }}
        >
            <style>{`
                @media print {
                    header[data-testid="main-navigation"],
                    a[data-testid="floating-whatsapp-btn"] { display: none !important; }
                    main[data-testid="investors-page"] { padding: 0 !important; }
                    .inv-page { page-break-after: always; }
                    .inv-page:last-child { page-break-after: auto; }
                    body { background: #fff !important; }
                }
                .inv-page {
                    max-width: 880px;
                    margin: 0 auto 48px;
                    padding: 48px 56px;
                    background: #0d0d0d;
                    border: 1px solid rgba(201,162,39,0.18);
                    box-shadow: 0 30px 80px rgba(0,0,0,0.4);
                }
                @media (max-width: 700px) {
                    .inv-page { padding: 28px 22px; }
                }
                .inv-eyebrow {
                    color: ${GOLD};
                    font-size: 10.5px;
                    letter-spacing: 0.34em;
                    text-transform: uppercase;
                    font-weight: 600;
                    margin: 0 0 14px;
                }
                .inv-h1 {
                    font-family: 'Playfair Display', serif;
                    font-weight: 400;
                    font-size: clamp(28px, 4.4vw, 42px);
                    line-height: 1.12;
                    letter-spacing: -0.015em;
                    margin: 0 0 18px;
                    color: #fff;
                }
                .inv-h2 {
                    font-family: 'Playfair Display', serif;
                    font-weight: 400;
                    font-size: 24px;
                    line-height: 1.22;
                    letter-spacing: -0.01em;
                    margin: 0 0 14px;
                    color: ${GOLD};
                }
                .inv-h3 {
                    font-family: 'Inter', sans-serif;
                    font-weight: 600;
                    font-size: 12px;
                    letter-spacing: 0.22em;
                    text-transform: uppercase;
                    color: ${GOLD};
                    margin: 24px 0 10px;
                }
                .inv-p {
                    font-size: 14.5px;
                    line-height: 1.78;
                    color: #d8d8d8;
                    margin: 0 0 14px;
                    font-weight: 300;
                }
                .inv-p strong { color: #fff; font-weight: 600; }
                .inv-ul { margin: 0 0 14px; padding: 0 0 0 18px; }
                .inv-ul li {
                    font-size: 14.5px;
                    line-height: 1.75;
                    color: #d8d8d8;
                    margin-bottom: 8px;
                    font-weight: 300;
                }
                .inv-ul li strong { color: #fff; font-weight: 600; }
                .inv-divider {
                    height: 1px;
                    background: linear-gradient(90deg, transparent, rgba(201,162,39,0.4), transparent);
                    margin: 32px 0;
                    border: 0;
                }
                .inv-stat-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 18px;
                    margin: 16px 0 6px;
                }
                .inv-stat {
                    border: 1px solid rgba(201,162,39,0.2);
                    padding: 18px 16px;
                    background: rgba(201,162,39,0.04);
                }
                .inv-stat .v {
                    font-family: 'Playfair Display', serif;
                    color: ${GOLD};
                    font-size: 22px;
                    line-height: 1.1;
                    margin: 0 0 4px;
                }
                .inv-stat .l {
                    font-size: 10.5px;
                    color: #aaa;
                    letter-spacing: 0.2em;
                    text-transform: uppercase;
                    margin: 0;
                    font-weight: 500;
                }
                .inv-callout {
                    border-left: 2px solid ${GOLD};
                    padding: 10px 16px;
                    margin: 18px 0;
                    background: rgba(201,162,39,0.05);
                    font-size: 14px;
                    line-height: 1.7;
                    color: #e8e8e8;
                    font-style: italic;
                    font-weight: 300;
                }
                .inv-stream {
                    display: grid;
                    grid-template-columns: 26px 1fr;
                    gap: 14px;
                    align-items: start;
                    padding: 14px 0;
                    border-bottom: 1px solid rgba(255,255,255,0.06);
                }
                .inv-stream:last-child { border-bottom: 0; }
                .inv-stream-num {
                    color: ${GOLD};
                    font-family: 'Playfair Display', serif;
                    font-size: 18px;
                    line-height: 1.1;
                }
                .inv-stream-title {
                    color: #fff;
                    font-size: 14px;
                    font-weight: 600;
                    letter-spacing: 0.04em;
                    margin: 0 0 4px;
                }
                .inv-stream-desc {
                    color: #bbb;
                    font-size: 13px;
                    line-height: 1.65;
                    margin: 0;
                    font-weight: 300;
                }
                .inv-img {
                    width: 100%;
                    display: block;
                    margin: 24px 0;
                    border: 1px solid rgba(201,162,39,0.18);
                    filter: grayscale(15%) contrast(1.02);
                }
                .inv-img-cap {
                    color: #888;
                    font-size: 11px;
                    letter-spacing: 0.22em;
                    text-transform: uppercase;
                    text-align: center;
                    margin: -14px 0 24px;
                    font-weight: 500;
                }
                .inv-img-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 14px;
                    margin: 22px 0 8px;
                }
                .inv-img-grid img {
                    width: 100%;
                    aspect-ratio: 4 / 3;
                    object-fit: cover;
                    border: 1px solid rgba(201,162,39,0.18);
                    display: block;
                    filter: grayscale(15%) contrast(1.02);
                }
                .inv-img-grid .cap {
                    text-align: center;
                    color: #888;
                    font-size: 10.5px;
                    letter-spacing: 0.22em;
                    text-transform: uppercase;
                    margin-top: 8px;
                    font-weight: 500;
                }
                .inv-sig {
                    font-family: 'Playfair Display', serif;
                    font-style: italic;
                    color: ${GOLD};
                    font-size: 22px;
                    margin: 6px 0 2px;
                }
            `}</style>

            {/* ============== COVER ============== */}
            <section className="inv-page" data-testid="inv-cover" style={{
                textAlign: "center",
                padding: "72px 48px",
            }}>
                <img
                    src={LOGO_URL}
                    alt="The Reset Sanctuary"
                    style={{ width: 96, height: 96, margin: "0 auto 28px", display: "block" }}
                />
                <p className="inv-eyebrow">The Reset Sanctuary · Investor Brief</p>
                <h1 className="inv-h1" style={{ margin: "0 auto 14px", maxWidth: 620 }}>
                    The Reset Sanctuary
                </h1>
                <p style={{
                    fontFamily: "Playfair Display, serif",
                    fontStyle: "italic",
                    color: GOLD,
                    fontSize: 18,
                    margin: "0 auto 32px",
                    maxWidth: 540,
                    lineHeight: 1.5,
                }}>
                    Precision Sound Recovery. <br/>A sanctuary for the nervous system.
                </p>
                <hr className="inv-divider" style={{ maxWidth: 360, margin: "32px auto" }}/>
                <p style={{
                    color: "#aaa",
                    fontSize: 13,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    margin: "0 0 6px",
                    fontWeight: 500,
                }}>
                    Business Plan & Investor Brief
                </p>
                <p style={{ color: "#777", fontSize: 12, margin: "0 0 32px" }}>
                    June 2026 · United Arab Emirates
                </p>
                <p style={{
                    color: GOLD,
                    fontSize: 10.5,
                    letterSpacing: "0.34em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    margin: 0,
                }}>
                    Hand-produced. Human. No AI.
                </p>
            </section>

            {/* ============== FOUNDER LETTER ============== */}
            <section className="inv-page" data-testid="inv-letter">
                <p className="inv-eyebrow">A Note from the Founder</p>
                <h2 className="inv-h2" style={{ fontSize: 30, color: "#fff", marginBottom: 24 }}>Before the numbers — a quick word.</h2>
                <p className="inv-p">
                    I built The Reset Sanctuary the same way I built every record that worked — slowly, by hand, by ear, in service of the person on the other end. No shortcuts, no algorithms, no synthetic shine.
                </p>
                <p className="inv-p">
                    What you'll read in the following pages is the operating reality: where TRS is today, what's already in motion, where it's going, and how capital accelerates that. The numbers are honest. The pipeline is real. The brand is mine.
                </p>
                <p className="inv-p">
                    My ask of any partner reading this is simple — meet the work first. Sit in a session. Watch the room go quiet. Then we'll talk structure, timeline, and ticket.
                </p>
                <p className="inv-p">
                    Thank you for the time. It's not lost on me what it's worth.
                </p>
                <p className="inv-sig">— Tatva K</p>
                <p style={{ color: "#888", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", margin: 0, fontWeight: 500 }}>
                    Founder · The Reset Sanctuary
                </p>
            </section>
            <section className="inv-page" data-testid="inv-opportunity">
                <p className="inv-eyebrow">01 · The Opportunity</p>
                <h2 className="inv-h1">A category-defining nervous-system recovery brand, built in the UAE — engineered to scale into a nine-figure global business.</h2>
                <p className="inv-p">
                    The Reset Sanctuary (TRS) is a Precision Sound Recovery practice founded by <strong>Tatva K</strong> — an artist with 150M+ aggregate streams, a 13-year radio background, and formal training from the Berklee College of Music — Music Therapy Masterclass, ISTA, and IPHM.
                </p>
                <p className="inv-p">
                    TRS occupies a rare position: the credibility of a recovery practice, the cultural reach of a globally streamed artist, and the operator instincts of a direct-response marketer. No incense. No woo-woo. Built for executives, athletes, and high-performance households.
                </p>
                <div className="inv-callout">
                    Hyperice. Therabody. Headspace. Calm. Othership. The global recovery category has minted multiple unicorns in the last seven years. None of them are founder-led by a globally streamed artist-practitioner. None of them are built from the UAE. TRS is.
                </div>
                <h3 className="inv-h3">Why now</h3>
                <ul className="inv-ul">
                    <li><strong>The global wellness market is $5.6T</strong>, and recovery is the fastest-growing vertical — projected to compound double-digits through 2030.</li>
                    <li><strong>UAE burnout is at a generational high.</strong> Executives, founders, and HNWI households are actively spending on recovery — not lifestyle wellness.</li>
                    <li><strong>Corporate wellness budgets are reopening</strong> post-2024 cost cuts, with measurable mental-health outcomes now mandatory in many group benefit reviews.</li>
                    <li><strong>Digital recovery products</strong> (Insight Timer, Calm, Headspace) have validated demand globally — but lack a credentialed UAE-built brand at the premium tier.</li>
                    <li><strong>Founder velocity:</strong> a fully operational two-location practice, a live consumer site, Meta Pixel + lead system, four print-ready B2B brochures, and an active corporate referral pipeline (NBF, IPHM, ISTA).</li>
                </ul>
            </section>

            {/* ============== THE FOUNDER ============== */}
            <section className="inv-page" data-testid="inv-founder">
                <p className="inv-eyebrow">02 · The Founder</p>
                <h2 className="inv-h1">Tatva K — artist, practitioner, operator.</h2>
                <img
                    src="/brochure-assets/tatva.png"
                    alt="Tatva K — Founder, The Reset Sanctuary"
                    className="inv-img"
                    style={{ aspectRatio: "16 / 10", objectFit: "cover" }}
                />
                <p className="inv-img-cap">Tatva K · Founder · The Reset Sanctuary</p>
                <p className="inv-p">
                    Tatva is the unfair advantage. He is one of the very few people in the world who is simultaneously a globally streamed recording artist, a formally trained sound-recovery practitioner, and a Halbert-trained direct-response operator. The combination is rare and difficult to replicate.
                </p>
                <h3 className="inv-h3">Industry credentials</h3>
                <ul className="inv-ul">
                    <li><strong>150M+ aggregate streams</strong> across catalogue (including major chart hits).</li>
                    <li><strong>13-year radio background</strong> — deep production craft, audio engineering, frequency design.</li>
                    <li><strong>TEDx Speaker</strong> · Featured by <strong>BBC</strong> and <strong>Rolling Stone</strong>.</li>
                </ul>
                <h3 className="inv-h3">Practitioner credentials</h3>
                <ul className="inv-ul">
                    <li><strong>Berklee College of Music</strong> — Music Therapy Masterclass alum.</li>
                    <li><strong>ISTA-certified</strong> Sound Recovery Practitioner.</li>
                    <li><strong>IPHM-accredited</strong> practitioner (International Practitioners of Holistic Medicine).</li>
                    <li><strong>Reiki Grandmaster.</strong></li>
                    <li><strong>Featured Practitioner</strong> on Insight Timer (world's largest meditation & recovery platform).</li>
                </ul>
                <h3 className="inv-h3">Operator credentials</h3>
                <ul className="inv-ul">
                    <li>Halbert-trained direct-response marketing — runs the funnel, the copy, the brand, and the production.</li>
                    <li>132K+ engaged personal IG audience (separate, intentionally kept clean).</li>
                    <li>Bootstrapped TRS from zero to a fully operational two-location practice with a live site, four B2B brochures, lead capture, and a corporate referral pipeline.</li>
                </ul>
                <div className="inv-callout">
                    "Hand-produced. Human. No AI." — the founder principle that defines every session, every track, and every interaction. In a market increasingly flooded with synthetic wellness, this is the moat.
                </div>
            </section>

            {/* ============== THE PROBLEM ============== */}
            <section className="inv-page" data-testid="inv-problem">
                <p className="inv-eyebrow">03 · The Problem</p>
                <h2 className="inv-h1">The UAE's most successful people are also the most depleted.</h2>
                <p className="inv-p">
                    The same population that drives the UAE's economy — founders, executives, HNWI families, professional athletes, real-estate principals, finance professionals — is operating under chronic nervous-system load: poor sleep, compounding stress, impaired focus, and silent burnout. Conventional wellness (yoga, meditation apps, gyms) is insufficient. Medical intervention is over-engineered for sub-clinical recovery.
                </p>
                <p className="inv-p">
                    There is a clear gap between <strong>"wellness consumer"</strong> and <strong>"medical patient"</strong> — and that gap is where premium recovery practices have built billion-dollar brands globally (Hyperice, Therabody, Othership). The UAE has no credentialed, founder-led equivalent in the sound-recovery vertical.
                </p>
            </section>

            {/* ============== THE SOLUTION ============== */}
            <section className="inv-page" data-testid="inv-solution">
                <p className="inv-eyebrow">04 · The Solution</p>
                <h2 className="inv-h1">Precision Sound Recovery — engineered frequencies, measurable downshift, repeatable outcomes.</h2>
                <p className="inv-p">
                    TRS delivers structured sound-recovery sessions designed to downshift the autonomic nervous system into parasympathetic recovery. Sessions combine engineered frequency design (drawn from the founder's production career), live instrumentation, and proprietary session architecture refined over years of practice.
                </p>
                <p className="inv-p">
                    Outcomes clients consistently report after a single session: <strong>deeper sleep, reduced anxiety load, sharper focus, lower resting heart rate, and a measurable sense of "reset"</strong> they cannot reproduce through conventional wellness.
                </p>

                <h3 className="inv-h3">Modalities & equipment</h3>
                <div className="inv-img-grid">
                    <div>
                        <img src="/brochure-assets/tuning-forks.jpg" alt="Calibrated tuning forks" />
                        <p className="cap">Calibrated Tuning Forks</p>
                    </div>
                    <div>
                        <img src="/brochure-assets/pemf-mat.jpg" alt="PEMF recovery mat" />
                        <p className="cap">PEMF Recovery Mat</p>
                    </div>
                </div>
                <p className="inv-p">
                    Sessions combine <strong>calibrated tuning forks</strong> (precision frequency application on neurological pressure points), a <strong>PEMF recovery mat</strong> (pulsed electromagnetic field for cellular recovery — used by NASA, professional athletes, and integrative medical practices), proprietary frequency tracks engineered in-studio, live instrumentation, and a refined session architecture. Equipment is professional-grade. Application is hand-produced.
                </p>

                <h3 className="inv-h3">Session formats currently delivered</h3>
                <ul className="inv-ul">
                    <li><strong>Private 1:1</strong> (in-villa, in-studio, or at HNWI homes)</li>
                    <li><strong>Couples & family sessions</strong></li>
                    <li><strong>Kids' sessions</strong> (focus, sleep, regulation)</li>
                    <li><strong>Group sessions</strong> (8–25 people)</li>
                    <li><strong>Corporate sessions</strong> (on-site, in-villa, or at TRS locations)</li>
                    <li><strong>Animal recovery</strong> — Equine, Camel, and Domestic Pets (a rare niche, particularly powerful in the UAE context)</li>
                </ul>
            </section>

            {/* ============== REVENUE STREAMS ============== */}
            <section className="inv-page" data-testid="inv-revenue">
                <p className="inv-eyebrow">05 · Revenue Streams</p>
                <h2 className="inv-h1">A layered revenue model. High-ticket service + recurring digital + corporate B2B.</h2>
                <p className="inv-p">
                    TRS is built as a portfolio, not a single product. Each layer feeds the next, and each layer has independent scale potential.
                </p>

                <div className="inv-stream">
                    <span className="inv-stream-num">01</span>
                    <div>
                        <p className="inv-stream-title">Private HNWI 1:1 Sessions</p>
                        <p className="inv-stream-desc">High-ticket recovery sessions delivered in-villa, in-clinic, or on-site for executives, families, and HNWI households across the UAE. Highest margin per hour.</p>
                    </div>
                </div>

                <div className="inv-stream">
                    <span className="inv-stream-num">02</span>
                    <div>
                        <p className="inv-stream-title">Corporate Programmes (B2B)</p>
                        <p className="inv-stream-desc">Executive 1:1s, team recovery, quarterly wellness programmes, and corporate retreats. Active referral pipeline initiated with the National Bank of Fujairah (NBF) to refer TRS to NBF's corporate banking clients. Four print-ready vertical brochures: Corporate, Real Estate, Gym & Recovery Centres, Athletes.</p>
                    </div>
                </div>

                <div className="inv-stream">
                    <span className="inv-stream-num">03</span>
                    <div>
                        <p className="inv-stream-title">Insight Timer — Global Digital Audience</p>
                        <p className="inv-stream-desc">Featured Practitioner on Insight Timer (the world's largest meditation & recovery platform — 30M+ active users). Recurring monetisation via Live Premium sessions, donations, and platform revenue. Target: AED 30–40K/month from Insight Timer alone within Month 3 of scaled releases.</p>
                    </div>
                </div>

                <div className="inv-stream">
                    <span className="inv-stream-num">04</span>
                    <div>
                        <p className="inv-stream-title">Digital Products (One-Time)</p>
                        <p className="inv-stream-desc">Sleep Visual Sound Bath (premium long-form video). High-margin, on-domain Stripe checkout, automated email delivery. Built once, sold infinitely. First product launching post-shoot.</p>
                    </div>
                </div>

                <div className="inv-stream">
                    <span className="inv-stream-num">05</span>
                    <div>
                        <p className="inv-stream-title">Online Courses</p>
                        <p className="inv-stream-desc">First course launch targeted around June 2026 — a multi-module signature programme for global students. Stripe checkout on-domain, evergreen delivery. Designed to compound brand authority and capture students into the TRS ecosystem.</p>
                    </div>
                </div>

                <div className="inv-stream">
                    <span className="inv-stream-num">06</span>
                    <div>
                        <p className="inv-stream-title">Group Sessions & Sound Baths</p>
                        <p className="inv-stream-desc">Recurring public and private group sessions at Al Hamra (RAK) and Al Furjan (Dubai). Strong word-of-mouth + Insight Timer follower conversion engine.</p>
                    </div>
                </div>

                <div className="inv-stream">
                    <span className="inv-stream-num">07</span>
                    <div>
                        <p className="inv-stream-title">Animal Recovery (Niche Moat)</p>
                        <p className="inv-stream-desc">Equine, Camel, and Domestic Pet sound recovery — exceptionally well-suited to the UAE market (equestrian estates, camel breeders, HNWI pet households). Unique cultural fit. High-margin, low-competition.</p>
                    </div>
                </div>

                <div className="inv-stream">
                    <span className="inv-stream-num">08</span>
                    <div>
                        <p className="inv-stream-title">Licensing & Brand Partnerships (Future)</p>
                        <p className="inv-stream-desc">Hotel residencies, hospitality partnerships, branded recovery audio for premium real-estate developments, automotive, and aviation. Already in early conversations with hospitality and real-estate partners.</p>
                    </div>
                </div>
            </section>

            {/* ============== TRACTION ============== */}
            <section className="inv-page" data-testid="inv-traction">
                <p className="inv-eyebrow">06 · Traction & Proof Points</p>
                <h2 className="inv-h1">Built quietly. Built credibly. Already in motion.</h2>
                <img
                    src="/brochure-assets/essam-poster.jpg"
                    alt="A live TRS session in progress"
                    className="inv-img"
                    style={{ aspectRatio: "16 / 10", objectFit: "cover" }}
                />
                <p className="inv-img-cap">A live TRS session · in-villa, UAE</p>

                <div className="inv-stat-grid">
                    <div className="inv-stat">
                        <p className="v">150M+</p>
                        <p className="l">Aggregate Streams (Founder Catalogue)</p>
                    </div>
                    <div className="inv-stat">
                        <p className="v">2 Locations</p>
                        <p className="l">Al Hamra, RAK · Al Furjan, Dubai</p>
                    </div>
                    <div className="inv-stat">
                        <p className="v">132K+</p>
                        <p className="l">Founder IG Audience (Engaged)</p>
                    </div>
                    <div className="inv-stat">
                        <p className="v">Live</p>
                        <p className="l">Site, Pixel, Lead Capture, 4 Brochures</p>
                    </div>
                </div>

                <h3 className="inv-h3">Distribution & pipeline already active</h3>
                <ul className="inv-ul">
                    <li><strong>NBF (National Bank of Fujairah)</strong> — referral partnership initiated with senior banker for corporate client wellness programmes. Mutual NDA pending.</li>
                    <li><strong>Insight Timer</strong> — Featured Practitioner status secured. Global digital audience activated.</li>
                    <li><strong>Direct B2B funnel</strong> — four vertical brochures live (Corporate, Real Estate, Gym & Recovery Centres, Athletes). Each carries WhatsApp + email lead capture.</li>
                    <li><strong>Direct-to-consumer funnel</strong> — `/sleep` landing page with Meta Pixel, standard events firing (ViewContent, Lead, Contact, CompleteRegistration).</li>
                    <li><strong>Private HNWI conversion page</strong> (`/session`) — vertical Essam session video + dual WhatsApp CTAs. Used for Dubai HNWI warm-share.</li>
                </ul>

                <h3 className="inv-h3">Brand & media credentials</h3>
                <ul className="inv-ul">
                    <li>Featured by <strong>BBC</strong>, <strong>TEDx</strong>, <strong>Rolling Stone</strong>.</li>
                    <li><strong>Berklee College of Music</strong> · <strong>ISTA</strong> · <strong>IPHM</strong> · <strong>Reiki Grandmaster</strong>.</li>
                    <li>Two-location physical practice with in-villa UAE-wide delivery.</li>
                </ul>
            </section>

            {/* ============== MARKET ============== */}
            <section className="inv-page" data-testid="inv-market">
                <p className="inv-eyebrow">07 · Market & Positioning</p>
                <h2 className="inv-h1">Premium recovery is the fastest-growing segment of global wellness. The UAE is the perfect launchpad.</h2>
                <p className="inv-p">
                    Global wellness is a $5.6T market growing at high-single-digit CAGR. Within that, <strong>mental wellness and recovery</strong> is the fastest-growing vertical, projected to compound double-digits through 2030 (McKinsey, GWI).
                </p>
                <p className="inv-p">
                    The UAE specifically has one of the highest concentrations of HNWI per capita globally, a young executive population under chronic stress, and a regulatory environment that actively welcomes premium wellness operators. Insight Timer alone has 30M+ global users — many UAE-based, ready to convert to live and online TRS programmes.
                </p>
                <h3 className="inv-h3">Where TRS sits</h3>
                <ul className="inv-ul">
                    <li><strong>Above</strong> standard meditation apps (Calm, Headspace) — TRS is founder-led, in-person, premium-tier.</li>
                    <li><strong>Adjacent</strong> to recovery-tech brands (Hyperice, Therabody) — but human, hand-produced, frequency-led.</li>
                    <li><strong>Distinct</strong> from yoga studios and wellness spas — TRS is recovery, not lifestyle.</li>
                </ul>
            </section>

            {/* ============== GROWTH ROADMAP ============== */}
            <section className="inv-page" data-testid="inv-roadmap">
                <p className="inv-eyebrow">08 · 12-Month Growth Roadmap</p>
                <h2 className="inv-h1">Compound layered revenue. Lock the brand. Build the audience.</h2>

                <h3 className="inv-h3">Quarter 1 (now → 90 days)</h3>
                <ul className="inv-ul">
                    <li>Launch first digital product (Sleep Visual Sound Bath) — Stripe on-domain.</li>
                    <li>Activate Insight Timer Live Premium cadence — target steady monthly recurring revenue.</li>
                    <li>Execute NBF corporate referral pipeline — first 2–3 anchor corporate clients.</li>
                    <li>Build `/live` Calendly + Stripe paid Zoom booking for global 1:1 HNWI sessions.</li>
                </ul>

                <h3 className="inv-h3">Quarter 2 (3 → 6 months)</h3>
                <ul className="inv-ul">
                    <li>Launch first signature online course (June 2026 target).</li>
                    <li>Expand corporate brochure portfolio with case studies (post-NDA NBF rollout).</li>
                    <li>Onboard a junior practitioner / session host for Dubai location to unlock founder bandwidth.</li>
                    <li>Begin hospitality partnership conversations (hotel residencies, branded recovery rooms).</li>
                </ul>

                <h3 className="inv-h3">Quarter 3–4 (6 → 12 months)</h3>
                <ul className="inv-ul">
                    <li>Open a flagship TRS recovery studio (location TBD — Dubai prime).</li>
                    <li>Build a small in-house production team (post, marketing, ops).</li>
                    <li>Expand digital product library — second and third course, sound libraries, app partnerships.</li>
                    <li>Begin international expansion exploration — KSA, UK, US.</li>
                </ul>
            </section>

            {/* ============== REVENUE TARGETS ============== */}
            <section className="inv-page" data-testid="inv-targets">
                <p className="inv-eyebrow">09 · Revenue Targets · Year 1–3</p>
                <h2 className="inv-h1">Layered revenue compounding from AED 4M+ to AED 30M+ in three years.</h2>
                <p className="inv-p">
                    These are operating targets — built bottoms-up from session pricing, conversion rates already observed on the existing funnels, and confirmed pipeline (NBF, Insight Timer, brochure inbound). Conservative case below. Stretch case is materially higher.
                </p>

                <div className="inv-stat-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                    <div className="inv-stat">
                        <p className="v">AED 4M+</p>
                        <p className="l">Year 1 · Run-Rate</p>
                    </div>
                    <div className="inv-stat">
                        <p className="v">AED 12M+</p>
                        <p className="l">Year 2 · Run-Rate</p>
                    </div>
                    <div className="inv-stat">
                        <p className="v">AED 30M+</p>
                        <p className="l">Year 3 · Run-Rate</p>
                    </div>
                </div>

                <h3 className="inv-h3">Year 1 — Foundation (Jun 2026 → Jun 2027) · ~AED 4M</h3>
                <ul className="inv-ul">
                    <li><strong>Private 1:1 HNWI sessions</strong> — ~AED 1.2M (40 sessions/mo at maturity, blended AED 2,500 ticket).</li>
                    <li><strong>Corporate B2B programmes</strong> — ~AED 1.5M (NBF anchor + 3–4 additional retainers).</li>
                    <li><strong>Insight Timer + Live Premium</strong> — ~AED 480K (~AED 40K/mo by Month 3).</li>
                    <li><strong>First online course launch</strong> — ~AED 600K (300 students, AED 1,997 ticket).</li>
                    <li><strong>Digital products + group sessions + animal recovery</strong> — ~AED 250K combined.</li>
                </ul>

                <h3 className="inv-h3">Year 2 — Scale (Jun 2027 → Jun 2028) · ~AED 12M</h3>
                <ul className="inv-ul">
                    <li><strong>Flagship Dubai studio</strong> live — content engine + group revenue + retreat hub.</li>
                    <li><strong>Corporate B2B</strong> 3x growth — post-NBF case study compounds referrals across UAE banking, real-estate, family-office channels.</li>
                    <li><strong>2–3 evergreen online courses</strong> — global reach via paid acquisition on `/sleep` Pixel + Insight Timer pipeline.</li>
                    <li><strong>First hospitality partnership</strong> live — branded TRS recovery room / hotel residency.</li>
                    <li><strong>Junior practitioners onboarded</strong> — unlocks founder bandwidth, multiplies billable hours.</li>
                </ul>

                <h3 className="inv-h3">Year 3 — Category (Jun 2028 → Jun 2029) · ~AED 30M</h3>
                <ul className="inv-ul">
                    <li><strong>International expansion</strong> — KSA, UK, and US market entry (founder community + diaspora demand).</li>
                    <li><strong>5+ evergreen digital products</strong> + signature multi-tier course library.</li>
                    <li><strong>Brand licensing</strong> — branded recovery audio for premium real-estate developments, aviation, and automotive.</li>
                    <li><strong>Hospitality residencies</strong> — multi-property partnerships across UAE and KSA.</li>
                    <li><strong>Speaking & keynote revenue</strong> — corporate, conference, and academic circuits compound founder authority.</li>
                </ul>
            </section>

            {/* ============== THE VISION ============== */}
            <section className="inv-page" data-testid="inv-vision">
                <p className="inv-eyebrow">10 · The Vision</p>
                <h2 className="inv-h1">A nine-figure global recovery brand — built from the UAE, founder-led, defensible by design.</h2>
                <p className="inv-p">
                    The five-year picture is not a UAE practice. It is a <strong>global recovery brand at the intersection of music, neuroscience, and premium wellness</strong> — with a credentialed founder at its centre, an audience compounding across IG, Insight Timer, course platforms, and hospitality partnerships, and a product layer that scales without the founder being in every room.
                </p>

                <div className="inv-stat-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                    <div className="inv-stat">
                        <p className="v">AED 100M+</p>
                        <p className="l">Year 5 · ARR Target</p>
                    </div>
                    <div className="inv-stat">
                        <p className="v">5 Countries</p>
                        <p className="l">UAE · KSA · UK · US · Singapore</p>
                    </div>
                    <div className="inv-stat">
                        <p className="v">10x+</p>
                        <p className="l">Comparable Exit Multiples on Revenue</p>
                    </div>
                </div>

                <h3 className="inv-h3">Reference exits</h3>
                <ul className="inv-ul">
                    <li><strong>Headspace Health</strong> — last valued at $3B.</li>
                    <li><strong>Calm</strong> — last valued at $2B.</li>
                    <li><strong>Hyperice</strong> — recovery hardware, $700M+ valuation.</li>
                    <li><strong>Therabody</strong> — recovery brand, raised at $800M+ valuation.</li>
                </ul>
                <p className="inv-p">
                    All four are recovery-adjacent. None are founder-led by a streamed recording artist. None are built from the Gulf. TRS is positioned to be the <strong>first credentialed, founder-led, sound-recovery brand to reach that comparable scale</strong> — with materially lower customer acquisition cost (organic founder reach + Insight Timer + IG) and materially higher brand defensibility (the founder cannot be cloned).
                </p>
                <div className="inv-callout">
                    The investor case is not the UAE business. The UAE business is the proof. The investor case is the global brand the UAE business unlocks.
                </div>
            </section>

            {/* ============== USE OF CAPITAL ============== */}
            <section className="inv-page" data-testid="inv-capital">
                <p className="inv-eyebrow">11 · Use of Capital</p>
                <h2 className="inv-h1">Capital accelerates what is already working.</h2>
                <p className="inv-p">
                    Capital is deployed to <strong>compress the timeline</strong> on a brand and audience that compound non-linearly. Categories of deployment, in order of priority:
                </p>
                <h3 className="inv-h3">1 · Flagship studio buildout</h3>
                <p className="inv-p">A purpose-built TRS recovery studio in prime Dubai — acoustically engineered, brand-controlled, photo/video-ready, capable of hosting both private 1:1s and group sessions. This becomes the operational and content hub.</p>

                <h3 className="inv-h3">2 · Audio + recording infrastructure</h3>
                <p className="inv-p">Pro-grade microphones, instruments, recording rig, and post-production capability to scale digital product output without compromising the "hand-produced, human, no AI" standard. The founder is the production team — investment compounds his output.</p>

                <h3 className="inv-h3">3 · Marketing & distribution</h3>
                <p className="inv-p">Paid Meta + Google scaling on the existing `/sleep` funnel (Pixel already live), Insight Timer paid placement, hospitality & real-estate partnership development, and PR retainer to convert existing earned-media credibility into compounding inbound.</p>

                <h3 className="inv-h3">4 · Team — focused, lean</h3>
                <p className="inv-p">A junior practitioner / session host (Dubai), a production assistant, and a part-time operations lead. Designed to unlock founder bandwidth, not to dilute the brand.</p>

                <h3 className="inv-h3">5 · Founder runway & working capital</h3>
                <p className="inv-p">A structured founder package and working capital buffer that allows the founder to operate full-time on TRS — focused on product, audience, and partnerships — without the noise of month-to-month cash management. In a founder-led brand, founder bandwidth is the single highest-ROI asset capital can protect.</p>
            </section>

            {/* ============== RISKS & MITIGATIONS ============== */}
            <section className="inv-page" data-testid="inv-risks">
                <p className="inv-eyebrow">12 · Risks & Mitigations</p>
                <h2 className="inv-h1">The honest map of where this can wobble — and how we hold it steady.</h2>
                <p className="inv-p">
                    Every founder-led brand carries category-specific risk. The table below names the four most material risks for TRS and the corresponding mitigation already in motion or built into the operating plan.
                </p>

                <h3 className="inv-h3">1 · Founder key-person risk</h3>
                <p className="inv-p">
                    <strong>Risk:</strong> The brand is built around Tatva K. Practitioner availability is the constraint on revenue per hour, and brand authority is concentrated in one person.<br/>
                    <strong>Mitigation:</strong> Year 1 onboards junior practitioners trained in the TRS session architecture. Year 2 builds an in-house production team. Digital products + course library convert founder hours into compounding, founder-independent revenue. Brand IP, founder bio, and session templates documented and owned by the company entity (not the individual).
                </p>

                <h3 className="inv-h3">2 · Regulatory positioning</h3>
                <p className="inv-p">
                    <strong>Risk:</strong> The wellness category is increasingly regulated globally. Language around health claims is closely policed.<br/>
                    <strong>Mitigation:</strong> TRS has adopted "Precision Sound Recovery" as its category language site-wide — deliberately distinct from medical claims. No diagnostic, therapeutic, or curative language is used in any client-facing material. Privacy policy, Meta Pixel disclosure, lead capture, and compliance copy are already in place. The brand is engineered to be defensible across UAE, UK, US, and EU regulatory regimes.
                </p>

                <h3 className="inv-h3">3 · Market category risk</h3>
                <p className="inv-p">
                    <strong>Risk:</strong> "Sound recovery" is a younger category than meditation or fitness. Education is part of the buyer journey.<br/>
                    <strong>Mitigation:</strong> The category gap is the opportunity — TRS captures category-defining positioning early. Founder media platform (BBC, TEDx, Rolling Stone, Insight Timer, 132K IG) is engineered to educate at scale. Insight Timer's 30M+ existing user base has already self-selected for recovery content — TRS sells into validated demand.
                </p>

                <h3 className="inv-h3">4 · Competitive entrants</h3>
                <p className="inv-p">
                    <strong>Risk:</strong> Larger wellness platforms or recovery hardware brands could attempt to enter sound recovery.<br/>
                    <strong>Mitigation:</strong> The defensible moat is the founder profile — globally streamed artist + formally trained practitioner + direct-response operator. That combination is functionally non-replicable. Add owned content library, owned audience, two-location UAE infrastructure, and an active corporate referral pipeline — and any competitor enters from materially behind.
                </p>
            </section>

            {/* ============== STRATEGIC ACQUIRERS ============== */}
            <section className="inv-page" data-testid="inv-acquirers">
                <p className="inv-eyebrow">13 · Strategic Acquirers & Exit Pathways</p>
                <h2 className="inv-h1">Multiple credible exit routes — each anchored by category fit.</h2>
                <p className="inv-p">
                    At AED 30M+ Year 3 run-rate and AED 100M+ Year 5 target, TRS sits squarely in the acquisition zone for four distinct strategic buyer categories. Each category has active M&A history in adjacent verticals and a clear strategic reason to acquire a credentialed, founder-led recovery brand with global digital distribution.
                </p>

                <h3 className="inv-h3">1 · Global Wellness Platforms</h3>
                <p className="inv-p">
                    <strong>Headspace Health · Calm · Insight Timer (parent).</strong> Buying TRS gives them a founder-led premium brand, a credentialed practitioner, and a high-ticket service layer to upsell their app users into. Headspace acquired Ginger for $3B; Calm has acquired multiple content brands. TRS fits this profile precisely.
                </p>

                <h3 className="inv-h3">2 · Recovery Hardware Brands</h3>
                <p className="inv-p">
                    <strong>Hyperice · Therabody · Whoop.</strong> Hardware-led recovery brands are aggressively acquiring software and content to differentiate against commoditisation. A founder-led sound-recovery brand with proprietary audio IP and a global audience is exactly the kind of acquisition Hyperice and Therabody have signalled they're looking at.
                </p>

                <h3 className="inv-h3">3 · Premium Hospitality Groups</h3>
                <p className="inv-p">
                    <strong>Six Senses · Bulgari Hotels · Aman · Mandarin Oriental · Marriott Luxury Group.</strong> Wellness is the fastest-growing line in luxury hospitality. Acquiring or partnering with TRS gives a hospitality group a signature, brand-named recovery experience for high-end properties globally. Six Senses has built its entire brand around this thesis.
                </p>

                <h3 className="inv-h3">4 · UAE / GCC Strategic & Sovereign Capital</h3>
                <p className="inv-p">
                    <strong>Mubadala (via partner platforms) · ADQ · regional family offices · GCC hospitality conglomerates.</strong> A UAE-built, founder-led, globally scalable wellness brand sits in the strategic interest zone of multiple regional strategics actively deploying capital into homegrown brand IP that can be exported.
                </p>

                <div className="inv-callout">
                    Acquisition is one path. IPO on a wellness-friendly exchange (LSE, Nasdaq) is a second. A founder-led perpetual-equity hold paying compounding dividends is a third. The brand is engineered so each of these remain open — not predetermined.
                </div>
            </section>

            {/* ============== THE ASK ============== */}
            <section className="inv-page" data-testid="inv-ask">
                <p className="inv-eyebrow">14 · The Ask</p>
                <h2 className="inv-h1">Open to a structured equity conversation with the right partner.</h2>
                <p className="inv-p">
                    TRS is at the stage where a thoughtful capital partner can meaningfully compress 24 months of execution into 6–9. We are open to discussing investment structure, ticket size, equity allocation, and any board / advisory involvement that aligns with how the partner typically deploys.
                </p>
                <p className="inv-p">
                    Given the strategic fit and the existing infrastructure already operating, we'd prefer to design the structure together rather than present a fixed term sheet. The underlying numbers — current revenue mix, gross margins, projected unit economics, and the 12-month and 3-year projections shared above — will support whatever shape the conversation takes.
                </p>
                <h3 className="inv-h3">What we can share next</h3>
                <ul className="inv-ul">
                    <li>Detailed financial model (revenue mix, gross margins, full P&amp;L projections).</li>
                    <li>Live audit of all current funnels, Meta Pixel data, and lead pipeline.</li>
                    <li>Walkthrough of the operational stack (CRM, brochures, production pipeline).</li>
                    <li>Founder availability for any due-diligence questions, on any timeline.</li>
                </ul>
            </section>

            {/* ============== CONTACT ============== */}
            <section className="inv-page" data-testid="inv-contact" style={{ textAlign: "center" }}>
                <img
                    src={LOGO_URL}
                    alt="The Reset Sanctuary"
                    style={{ width: 72, height: 72, margin: "0 auto 24px", display: "block" }}
                />
                <p className="inv-eyebrow">Direct Founder Contact</p>
                <h2 className="inv-h1" style={{ fontSize: 30 }}>Tatva K</h2>
                <p style={{ color: GOLD, fontSize: 13, letterSpacing: "0.22em", textTransform: "uppercase", margin: "0 0 28px", fontWeight: 600 }}>
                    Founder · The Reset Sanctuary
                </p>
                <p className="inv-p" style={{ textAlign: "center" }}>
                    {PHONE_DISPLAY}<br/>
                    {EMAIL}<br/>
                    theresetsanctuary.org
                </p>
                <hr className="inv-divider" style={{ maxWidth: 280, margin: "32px auto" }}/>
                <p style={{
                    color: GOLD,
                    fontSize: 10.5,
                    letterSpacing: "0.34em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    margin: 0,
                }}>
                    Hand-produced. Human. No AI.
                </p>
            </section>
        </main>
    );
};

export default Investors;
